import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, query, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, auth, storage } from '../../firebase';
import './UploadBankStatements.css';

function UploadBankStatements({ isOpen, onClose, onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [customFileName, setCustomFileName] = useState('');
  
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const user = auth.currentUser;

  const categories = [
    { label: 'Bank Statements', value: 'bank_statements', needsAccount: true, needsCustomName: false },
    { label: 'Receipts', value: 'receipts', needsAccount: true, needsCustomName: false },
    { label: 'Reports', value: 'reports', needsAccount: false, needsCustomName: true },
    { label: 'Documents', value: 'documents', needsAccount: false, needsCustomName: true },
    { label: 'Other', value: 'others', needsAccount: false, needsCustomName: true }
  ];

  // Load accounting periods
  useEffect(() => {
    if (isOpen && user) {
      loadAccountingPeriods();
      loadAccounts();
    }
  }, [isOpen, user]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedCategory('');
    setSelectedPeriod('');
    setSelectedAccount('');
    setCustomFileName('');
  };

  const loadAccountingPeriods = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const periodsRef = collection(db, 'users', user.uid, 'Accounting_Periods');
      const q = query(periodsRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);

      const currentDate = new Date();
      currentDate.setDate(1);
      currentDate.setHours(0, 0, 0, 0);

      const loadedPeriods = [];

      snapshot.forEach(doc => {
        const dateField = doc.data().date;
        if (!dateField) return;

        try {
          const date = new Date(dateField);
          date.setDate(1);
          date.setHours(0, 0, 0, 0);

          // Only include periods that are not in the future
          if (date > currentDate) return;

          const displayName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          loadedPeriods.push({ displayName, monthKey });
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      });

      // Remove duplicates based on monthKey
      const uniquePeriods = loadedPeriods.filter((period, index, self) =>
        index === self.findIndex(p => p.monthKey === period.monthKey)
      );

      setPeriods(uniquePeriods);
    } catch (error) {
      console.error('Error loading periods:', error);
      alert('Failed to load accounting periods');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    if (!user) return;

    try {
      const accountsRef = collection(db, 'users', user.uid, 'account_setup');
      const q = query(accountsRef, where('active', '==', true));
      const snapshot = await getDocs(q);

      const loadedAccounts = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const name = data.name;
        const hasFiles = data.files !== false; // Default to true if field doesn't exist

        // Only add accounts that have files = true
        if (hasFiles && name) {
          loadedAccounts.push(name);
        }
      });

      loadedAccounts.sort();
      setAccounts(loadedAccounts);
    } catch (error) {
      console.error('Error loading accounts:', error);
      alert('Failed to load accounts');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    // Reset account and custom name when category changes
    setSelectedAccount('');
    setCustomFileName('');
  };

  const getSelectedCategoryConfig = () => {
    return categories.find(cat => cat.value === selectedCategory);
  };

  const validateForm = () => {
    if (!selectedFile) {
      alert('Please select a file');
      return false;
    }

    if (!selectedCategory) {
      alert('Please select a category');
      return false;
    }

    if (!selectedPeriod) {
      alert('Please select a period');
      return false;
    }

    const categoryConfig = getSelectedCategoryConfig();

    if (categoryConfig?.needsAccount && !selectedAccount) {
      alert('Please select an account');
      return false;
    }

    // Custom name is MANDATORY for categories that support it
    if (categoryConfig?.needsCustomName && !customFileName.trim()) {
      alert('Please enter a file name');
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!validateForm() || !user) return;

    setUploading(true);

    try {
      // Get file extension
      const extension = selectedFile.name.split('.').pop();
      
      // Generate final filename
      const categoryConfig = getSelectedCategoryConfig();
      let finalFileName;

      if (categoryConfig?.needsAccount) {
        // Auto-generate: AccountName_YYYY-MM.ext
        finalFileName = `${selectedAccount}_${selectedPeriod}.${extension}`;
      } else {
        // Custom name (mandatory for these categories)
        finalFileName = `${customFileName.trim()}.${extension}`;
      }

      // Storage path
      const storagePath = `users/${user.uid}/cabinet/${selectedCategory}/${selectedPeriod}/${finalFileName}`;
      const storageRef = ref(storage, storagePath);

      // Upload file to Storage
      await uploadBytes(storageRef, selectedFile);

      // Save metadata to Firestore
      const fileKey = categoryConfig?.needsAccount 
        ? selectedAccount 
        : finalFileName.replace(/\./g, '_');

      const fileData = {
        [fileKey]: {
          fileName: finalFileName,
          storagePath: storagePath,
          uploadedAt: new Date()
        }
      };

      const monthDocRef = doc(db, 'users', user.uid, 'cabinet', 'files', selectedCategory, selectedPeriod);
      await setDoc(monthDocRef, fileData, { merge: true });

      alert('File uploaded successfully!');
      
      // Call success callback to refresh parent component
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Close modal and reset form
      onClose();
      resetForm();

    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const categoryConfig = getSelectedCategoryConfig();

  return (
    <div className="upload-modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal-header">
          <h2>📤 Upload File</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="upload-modal-body">
          {/* File Selection */}
          <div className="form-group">
            <label className="form-label">Select File *</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="fileInput"
                onChange={handleFileChange}
                accept="*/*"
                className="file-input"
              />
              <label htmlFor="fileInput" className="file-input-label">
                {selectedFile ? (
                  <span className="file-selected">
                    📄 {selectedFile.name}
                  </span>
                ) : (
                  <span className="file-placeholder">
                    📁 Choose a file...
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* Category Selection */}
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select 
              className="form-select"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Period Selection */}
          {selectedCategory && (
            <div className="form-group">
              <label className="form-label">Period *</label>
              <select 
                className="form-select"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                disabled={loading}
              >
                <option value="">Select period...</option>
                {periods.map(period => (
                  <option key={period.monthKey} value={period.monthKey}>
                    {period.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Account Selection (for bank statements & receipts) */}
          {selectedCategory && categoryConfig?.needsAccount && (
            <div className="form-group">
              <label className="form-label">Account *</label>
              <select 
                className="form-select"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <option value="">Select account...</option>
                {accounts.map(account => (
                  <option key={account} value={account}>
                    {account}
                  </option>
                ))}
              </select>
              {accounts.length === 0 && (
                <p className="form-hint error">No accounts available for file uploads</p>
              )}
            </div>
          )}

          {/* Custom File Name (for reports, documents, others) */}
          {selectedCategory && categoryConfig?.needsCustomName && (
            <div className="form-group">
              <label className="form-label">File Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter a name for this file"
                value={customFileName}
                onChange={(e) => setCustomFileName(e.target.value)}
              />
              <p className="form-hint">Extension will be added automatically</p>
            </div>
          )}

          {/* File Preview Info */}
          {selectedFile && selectedCategory && selectedPeriod && (
            <div className="file-preview">
              <p className="preview-label">Preview:</p>
              <div className="preview-details">
                <p><strong>File:</strong> {selectedFile.name}</p>
                <p><strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB</p>
                <p><strong>Category:</strong> {categoryConfig?.label}</p>
                <p><strong>Period:</strong> {periods.find(p => p.monthKey === selectedPeriod)?.displayName}</p>
                {categoryConfig?.needsAccount && selectedAccount && (
                  <p><strong>Account:</strong> {selectedAccount}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="upload-modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadBankStatements;